import { Link, router } from "@inertiajs/react";
import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, BookOpen, CheckCheck, ChevronDown, ChevronUp, ExternalLink, Mail, MapPin, Search, ShieldAlert, UserX } from "lucide-react";
import AppLayout from "../../layouts/AppLayout";
import { Button } from "../../components/ui/button";
import FirmRelationshipCard, { FirmRelationship } from "../../components/FirmRelationshipCard";
import AskPermissionButton from "../../components/AskPermissionButton";
import type { LeopardResult, SearchParams } from "./_types";
import FilterModal from "./FilterModal";
import { useAiJobStatus } from "../../hooks/useAiJobStatus";

interface AiJobRef {
  id:    number;
  state: "pending" | "running" | "ready" | "failed";
  error: string | null;
}

interface DncRecord {
  firm_name: string;
  scope_type: string;
  end_date: string | null;
  is_ongoing: boolean;
}

interface Job {
  id: number;
  firm_name: string;
  title: string;
  locations: string[];
  practice_area: string;
  jd_year_range: string;
  job_type: string;
  status: string;
  sourcing_status: string;
  job_alert_id: number | null;
  leopard_job_id: string | null;
  non_recruiter: boolean;
  notification_type: "new_job" | "closed_job" | "changed_job" | null;
  first_seen_at: string | null;
  last_seen_at: string | null;
  last_searched_at: string | null;
  description: string | null;
  skip_reason: string | null;
  dnc_firm_status: string;
  dnc_record: DncRecord | null;
}

interface Alert {
  id: number;
  subject: string | null;
  alert_type: "new_job" | "closed_job" | "changed_job";
  matched_at: string | null;
}

const ALERT_TYPE_CONFIG: Record<string, { label: string; className: string }> = {
  new_job:     { label: "New",     className: "bg-green-100 text-green-700 border-green-200" },
  closed_job:  { label: "Closed",  className: "bg-red-100 text-red-700 border-red-200" },
  changed_job: { label: "Changed", className: "bg-amber-100 text-amber-700 border-amber-200" },
};

function formatTimestamp(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

// tier is either a new Slice-4 tier (strong/possible/weak) or a legacy
// pre-rematch value (high/medium/low) that hasn't gone through
// Jobs::TieredMatcher yet — TIER_CONFIG below maps both onto the same display.
type Tier = "strong" | "possible" | "weak" | "high" | "medium" | "low";

interface MatchResult {
  candidate_id: number;
  full_name: string;
  first_name: string;
  email: string;
  firm_name: string;
  practice_area: string;
  jd_year: number | null;
  location: string;
  pipeline_stage: string;
  days_since_update: number;
  tier: Tier;
  rationale: string;
  submission_id: number | null;
  submission_status: string | null;
}

interface ExcludedResult {
  candidate_id: number;
  full_name: string;
  firm_name: string;
  practice_area: string;
  reason: string;
}

interface Campaign {
  id: number;
  status: string;
  total_count: number;
  cleared_count: number;
  blocked_count: number;
}

interface Props {
  job: Job;
  alert: Alert | null;
  matches: MatchResult[];
  excluded: ExcludedResult[];
  campaign: Campaign | null;
  firm_relationship: FirmRelationship | null;
  search_params: SearchParams;
  leopard_results: LeopardResult[];
  ai_assist: boolean;
  leopard_search_job: AiJobRef | null;
}

// New tiers map straight through; legacy high/medium/low (pre-rematch rows)
// map onto the same three buckets so old and new data render identically
// until every job has been through Jobs::TieredMatcher.
const TIER_CONFIG: Record<Tier, { label: string; className: string }> = {
  strong:   { label: "Strong",   className: "bg-green-100 text-green-700" },
  high:     { label: "Strong",   className: "bg-green-100 text-green-700" },
  possible: { label: "Possible", className: "bg-amber-100 text-amber-700" },
  medium:   { label: "Possible", className: "bg-amber-100 text-amber-700" },
  weak:     { label: "Weak",     className: "bg-gray-100 text-gray-500" },
  low:      { label: "Weak",     className: "bg-gray-100 text-gray-500" },
};

const WEAK_TIERS = new Set<Tier>(["weak", "low"]);
const STRONG_TIERS = new Set<Tier>(["strong", "high"]);

const STAGE_LABELS: Record<string, string> = {
  new_lead: "New Lead", intake_scheduled: "Intake", resume_editing: "Resume",
  package_pending: "Package", submitted: "Submitted", screener: "Screener",
  full_round: "Full Round", offer_placed: "Placed",
};

const STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  open:           { label: "Open",          className: "bg-green-100 text-green-700 border-green-200" },
  closed:         { label: "Closed",        className: "bg-red-100 text-red-600 border-red-200" },
  no_change:      { label: "No Change",     className: "bg-gray-100 text-gray-500 border-gray-200" },
  direct_hire:    { label: "Direct Hire",   className: "bg-amber-100 text-amber-700 border-amber-200" },
  not_recruiting: { label: "Not Recruiting",className: "bg-gray-100 text-gray-500 border-gray-200" },
};

const SOURCING_CONFIG: Record<string, { label: string; className: string }> = {
  pending_evaluation: { label: "Pending Eval",   className: "bg-yellow-50 text-yellow-700 border-yellow-200" },
  worth_sourcing:     { label: "Worth Sourcing", className: "bg-blue-50 text-blue-700 border-blue-200" },
  sourced:            { label: "Sourced",        className: "bg-green-50 text-green-700 border-green-200" },
  skipped:            { label: "Skipped",        className: "bg-gray-100 text-gray-400 border-gray-200" },
  failed:             { label: "Failed",         className: "bg-red-50 text-red-600 border-red-200" },
};

const CAMPAIGN_STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  draft_review: { label: "Draft Review", className: "bg-yellow-50 text-yellow-700 border-yellow-200" },
  approved:     { label: "Approved",     className: "bg-blue-50 text-blue-700 border-blue-200" },
  sending:      { label: "Sending",      className: "bg-blue-50 text-blue-700 border-blue-200" },
  sent:         { label: "Sent",         className: "bg-green-50 text-green-700 border-green-200" },
  cancelled:    { label: "Cancelled",    className: "bg-gray-100 text-gray-500 border-gray-200" },
};

const DNC_BANNER_CONFIG: Record<string, { label: string; className: string }> = {
  nationwide_block: { label: "Nationwide DNC Block",  className: "bg-red-50 border-red-200 text-red-800" },
  office_block:     { label: "Office DNC Block",      className: "bg-amber-50 border-amber-200 text-amber-800" },
  needs_review:     { label: "DNC — Needs Review",    className: "bg-gray-50 border-gray-200 text-gray-700" },
};

function csrfToken(): string {
  return (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? "";
}

function relativeTime(iso: string | null): string {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86_400_000);
  if (days === 0) return "today";
  if (days === 1) return "yesterday";
  if (days < 30) return `${days}d ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 8) return `${weeks}w ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function StatusBadge({ value, config }: { value: string; config: Record<string, { label: string; className: string }> }) {
  const cfg = config[value];
  if (!cfg) return <span className="text-xs text-gray-400">{value}</span>;
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded border ${cfg.className}`}>
      {cfg.label}
    </span>
  );
}

function DncBanner({ job }: { job: Job }) {
  const cfg = DNC_BANNER_CONFIG[job.dnc_firm_status];
  if (!cfg) return null;

  const rec = job.dnc_record;
  return (
    <div className={`rounded-lg border px-5 py-3 mb-6 ${cfg.className}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <ShieldAlert className="w-4 h-4 shrink-0" />
        <div>
          <div className="font-medium text-sm">{cfg.label} — {rec?.firm_name ?? job.firm_name}</div>
          {rec && (
            <div className="text-xs mt-0.5">
              Scope: {rec.scope_type}
              {rec.is_ongoing ? " · Ongoing" : rec.end_date ? ` · Until ${new Date(rec.end_date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}` : ""}
            </div>
          )}
        </div>
        <Link href="/dnc" className="ml-auto flex items-center gap-1 text-xs font-medium hover:underline">
          View DNC records <ExternalLink className="w-3 h-3" />
        </Link>
      </div>
    </div>
  );
}

function CampaignBanner({ campaign }: { campaign: Campaign }) {
  const cfg = CAMPAIGN_STATUS_CONFIG[campaign.status] ?? { label: campaign.status, className: "bg-gray-100 text-gray-500 border-gray-200" };
  return (
    <div className="bg-white rounded-lg border border-gray-200 px-5 py-3 mb-6 flex items-center gap-4 flex-wrap">
      <Mail className="w-4 h-4 text-gray-400 shrink-0" />
      <span className="text-sm font-medium text-gray-700">Email Campaign</span>
      <span className={`text-xs font-medium px-2 py-0.5 rounded border ${cfg.className}`}>{cfg.label}</span>
      <div className="flex items-center gap-3 text-xs text-gray-500 ml-1">
        <span><span className="font-medium text-gray-700">{campaign.total_count}</span> total</span>
        <span><span className="font-medium text-green-700">{campaign.cleared_count}</span> cleared</span>
        <span><span className="font-medium text-red-600">{campaign.blocked_count}</span> blocked</span>
      </div>
      <Link href={`/email-campaigns/${campaign.id}`}
        className="ml-auto flex items-center gap-1 text-xs text-green-700 hover:text-green-800 font-medium">
        View campaign <ExternalLink className="w-3 h-3" />
      </Link>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs text-gray-400 uppercase tracking-wide mb-0.5">{label}</div>
      <div className="text-sm text-gray-800">{children}</div>
    </div>
  );
}

function FilterChip({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center text-xs bg-gray-100 text-gray-600 border border-gray-200 rounded px-2 py-0.5">
      {label}
    </span>
  );
}

function LeopardSearchPanel({ searchParams, jobId }: {
  searchParams: SearchParams;
  jobId:        number;
}) {
  const [modalOpen, setModalOpen] = useState(false);

  function saveFilters(updated: SearchParams) {
    router.patch(`/job-alerts/${jobId}/search-params`,
      { search_params: updated as unknown as string },
      { onSuccess: () => setModalOpen(false) },
    );
  }

  const sp            = searchParams;
  const practiceAreas = sp.practice_area?.include ?? [];
  const specialties   = sp.specialties?.include ?? [];
  const specialtySource = sp.specialty_resolution?.source;
  const incStates      = sp.locations?.include?.states       ?? [];
  const incCities      = sp.locations?.include?.cities       ?? [];
  const incStateCities = sp.locations?.include?.state_cities ?? [];
  const cityStateChips = incStateCities.map((sc) => {
    const [state, city] = sc.split(";");
    return `${city} (${state})`;
  });
  const citiesInPairs  = new Set(incStateCities.map((sc) => sc.split(";")[1]));
  const orphanCities   = incCities.filter((c) => !citiesInPairs.has(c));
  const locations      = [...incStates, ...cityStateChips, ...orphanCities];
  const types          = sp.candidate_types ?? [];
  const jdMin          = sp.jd_year?.min;
  const jdMax          = sp.jd_year?.max;
  const firmTier       = sp.firm_tier;
  const schoolTier     = sp.law_school_tier;
  const tenureMin     = sp.tenure_min ?? 0;

  return (
    <>
      <div className="border-t border-gray-100 px-5 py-4">
        <div className="flex items-center gap-1.5 text-xs text-gray-500 mb-3">
          <Search className="w-3.5 h-3.5" />
          Leopard search parameters
        </div>

        <div className="flex flex-wrap gap-1.5 mb-3">
          {practiceAreas.length > 0 && <FilterChip label={`Practice: ${practiceAreas.join(", ")}`} />}
          {specialties.length > 0   && (
            <FilterChip label={`Specialty: ${specialties.join(", ")}${specialtySource === "alias" || specialtySource === "ai" ? " (AI)" : ""}`} />
          )}
          {locations.length > 0      && <FilterChip label={`Locations: ${locations.join(", ")}`} />}
          {types.length > 0          && <FilterChip label={`Types: ${types.join(", ")}`} />}
          {(jdMin || jdMax)          && <FilterChip label={`JD: ${jdMin ?? "?"} – ${jdMax ?? "?"}`} />}
          {tenureMin > 0             && <FilterChip label={`Tenure: ${tenureMin}+ mo`} />}
          <FilterChip label={`Firm: ${firmTier == null ? "Any" : `Top ${firmTier}`}`} />
          <FilterChip label={`School: ${schoolTier == null ? "Any" : `Top ${schoolTier}`}`} />
          {practiceAreas.length === 0 && specialties.length === 0 && locations.length === 0 && types.length === 0 && !jdMin && !jdMax && (
            <span className="text-xs text-gray-400 italic">No filters configured</span>
          )}
        </div>

        <Button variant="outline" size="sm" onClick={() => setModalOpen(true)}>
          Edit filters
        </Button>
      </div>

      <FilterModal
        open={modalOpen}
        searchParams={searchParams}
        onSave={saveFilters}
        onClose={() => setModalOpen(false)}
      />
    </>
  );
}

// DNC badge for search results (task 73193596 follow-up) — set at search time.
// Clear renders nothing (no badge = fine); only flag blocked/tentative/unchecked.
const DNC_BADGE: Record<string, { label: string; className: string } | null> = {
  clear:               null,
  blocked:             { label: "DNC",       className: "bg-red-100 text-red-700" },
  tentatively_blocked: { label: "DNC?",      className: "bg-amber-100 text-amber-700" },
  unchecked:           { label: "Unchecked", className: "bg-gray-100 text-gray-500" },
};

function LeopardResultsSection({ results, lastSearchedAt, jobId, hasCampaign }: {
  results: LeopardResult[];
  lastSearchedAt: string | null;
  jobId: number;
  hasCampaign: boolean;
}) {
  const [creating, setCreating] = useState(false);
  const [checkingRemaining, setCheckingRemaining] = useState(false);
  const [showUnchecked, setShowUnchecked] = useState(false);
  if (!lastSearchedAt && results.length === 0) return null;

  // All results are persisted; only the top N are DNC-checked at search time.
  // Show the DNC-checked set by default; the unchecked remainder collapses
  // behind a toggle + a "Check remaining" action (task 73193596).
  const unchecked = results.filter((r) => r.dnc_status === "unchecked");
  const checked   = results.filter((r) => r.dnc_status !== "unchecked");
  const visible   = showUnchecked ? results : checked;

  function createCampaign() {
    setCreating(true);
    router.post(`/job-alerts/${jobId}/create_campaign`, {}, { onFinish: () => setCreating(false) });
  }

  function checkRemaining() {
    setCheckingRemaining(true);
    router.post(`/job-alerts/${jobId}/check_remaining_dnc`, {}, { onFinish: () => setCheckingRemaining(false) });
  }

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-gray-700">
          Leopard Results{" "}
          <span className="text-gray-400 font-normal">({results.length})</span>
        </h2>
        <div className="flex items-center gap-3">
          {lastSearchedAt && (
            <span className="text-xs text-gray-400">Last searched {relativeTime(lastSearchedAt)}</span>
          )}
          {results.length > 0 && (
            hasCampaign ? (
              <span className="text-xs text-gray-400">Campaign created</span>
            ) : (
              <Button variant="default" size="sm" onClick={createCampaign} disabled={creating}>
                <Mail className="w-3.5 h-3.5 mr-1.5" />
                {creating ? "Creating…" : "Create campaign"}
              </Button>
            )
          )}
        </div>
      </div>

      {unchecked.length > 0 && (
        <div className="flex items-center gap-3 mb-2">
          <Button variant="outline" size="sm" onClick={() => setShowUnchecked((s) => !s)}>
            {showUnchecked ? "Hide" : "Show"} {unchecked.length} unchecked
          </Button>
          <Button variant="outline" size="sm" onClick={checkRemaining} disabled={checkingRemaining}>
            {checkingRemaining ? "Checking…" : `Check DNC for remaining (${unchecked.length})`}
          </Button>
          <span className="text-xs text-gray-400">Unchecked results are still DNC-screened before any campaign send.</span>
        </div>
      )}

      {results.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 py-8 text-center">
          <p className="text-sm text-gray-400">No results from last search.</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
          {visible.map((r) => {
            const fullName = [r.first_name, r.last_name].filter(Boolean).join(" ") || r.email;
            return (
              <div key={r.id} className="px-4 py-3 flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-gray-900">{fullName}</span>
                    {DNC_BADGE[r.dnc_status] && (
                      <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${DNC_BADGE[r.dnc_status]!.className}`}>
                        {DNC_BADGE[r.dnc_status]!.label}
                      </span>
                    )}
                    {r.jd_year && <span className="text-xs text-gray-400">JD {r.jd_year}</span>}
                    {r.law_school && (
                      <span className="text-xs bg-gray-50 border border-gray-200 text-gray-500 px-1.5 py-0.5 rounded">
                        {r.law_school}
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {r.firm_title && <>{r.firm_title} · </>}
                    {r.firm_name}
                    {r.practice_area && <> · {r.practice_area}</>}
                    {r.location && <> · {r.location}</>}
                  </p>
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {r.phone && <span className="text-xs text-gray-400">{r.phone}</span>}
                  {r.leopard_link && (
                    <a href={r.leopard_link} target="_blank" rel="noopener noreferrer"
                      className="text-xs text-green-700 hover:text-green-800 flex items-center gap-0.5">
                      Leopard <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                  {r.linkedin_url && (
                    <a href={r.linkedin_url} target="_blank" rel="noopener noreferrer"
                      className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-0.5">
                      LinkedIn <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function JobCard({ job, alert, undraftedCount, onDraftAll, draftingAll, searchParams, strongCount, possibleCount }: {
  job: Job;
  alert: Alert | null;
  undraftedCount: number;
  onDraftAll: () => void;
  draftingAll: boolean;
  searchParams: SearchParams;
  strongCount: number;
  possibleCount: number;
}) {
  const [showDescription, setShowDescription] = useState(false);

  return (
    <div className="bg-white rounded-lg border border-gray-200 mb-6 overflow-hidden">
      <div className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-xl font-bold text-gray-900 leading-tight">{job.firm_name}</h1>
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <StatusBadge value={job.status} config={STATUS_CONFIG} />
              <StatusBadge value={job.sourcing_status} config={SOURCING_CONFIG} />
            </div>
          </div>

          <div className="flex flex-col items-end gap-3 shrink-0">
            <div className="text-right">
              {/* Tier breakdown, not the raw persisted-row count (task b5341df6) —
                  match_count includes Weak, and surfacing it raw re-inflates the
                  "123 matched" problem this overhaul exists to fix. */}
              <div className="text-3xl font-bold text-gray-900 leading-none">
                {strongCount}<span className="text-gray-300 font-normal">/{possibleCount}</span>
              </div>
              <div className="text-xs text-gray-400 mt-0.5">strong / possible</div>
            </div>
            {undraftedCount > 0 && (
              <Button size="sm" onClick={onDraftAll} disabled={draftingAll}
                className="bg-green-700 hover:bg-green-800 text-white">
                <CheckCheck className="w-3.5 h-3.5 mr-1" />
                {draftingAll ? "Drafting..." : `Draft All (${undraftedCount})`}
              </Button>
            )}
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <Field label="Practice">{job.practice_area || "—"}</Field>
          <Field label="JD Year">{job.jd_year_range || "—"}</Field>
          <Field label="Location">
            {job.locations.length > 0
              ? <span className="flex items-center gap-1"><MapPin className="w-3 h-3 text-gray-400 shrink-0" />{job.locations.join(", ")}</span>
              : "—"}
          </Field>
          <Field label="Type">{job.job_type && job.job_type.length <= 40 ? job.job_type : "—"}</Field>
          <Field label="First Seen">{relativeTime(job.first_seen_at)}</Field>
          {job.skip_reason && <Field label="Skip Reason"><span className="text-amber-600">{job.skip_reason}</span></Field>}
        </div>
      </div>

      <LeopardSearchPanel
        searchParams={searchParams}
        jobId={job.id}
      />

      {(job.description || alert) && (
        <div className="border-t border-gray-100">
          <button
            onClick={() => setShowDescription((p) => !p)}
            className="w-full flex items-center justify-between px-5 py-3 text-xs text-gray-500 hover:bg-gray-50 transition-colors"
          >
            <span>View original alert</span>
            {showDescription ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
          {showDescription && (
            <div className="px-5 pb-4 space-y-3">
              <dl className="text-xs grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1.5 bg-gray-50 rounded p-3 border border-gray-100">
                {alert?.subject && (<>
                  <dt className="text-gray-500">Subject</dt>
                  <dd className="text-gray-700">{alert.subject}</dd>
                </>)}
                {job.notification_type && (<>
                  <dt className="text-gray-500" title="Per-job categorization from the email's Notification(s) line. Independent of the posting's current Status above.">Notification</dt>
                  <dd>
                    <span className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium border ${ALERT_TYPE_CONFIG[job.notification_type]?.className ?? "bg-gray-100 text-gray-600 border-gray-200"}`}>
                      {ALERT_TYPE_CONFIG[job.notification_type]?.label ?? job.notification_type}
                    </span>
                  </dd>
                </>)}
                {job.leopard_job_id && (<>
                  <dt className="text-gray-500">Leopard ID</dt>
                  <dd className="text-gray-700 font-mono">{job.leopard_job_id}</dd>
                </>)}
                {job.non_recruiter && (<>
                  <dt className="text-gray-500">Source</dt>
                  <dd className="text-gray-700">Non-recruiter contact</dd>
                </>)}
                {job.last_searched_at && (<>
                  <dt className="text-gray-500">Last Leopard pull</dt>
                  <dd className="text-gray-700">{formatTimestamp(job.last_searched_at)}</dd>
                </>)}
                {alert?.matched_at && (<>
                  <dt className="text-gray-500">Last AI match</dt>
                  <dd className="text-gray-700">{formatTimestamp(alert.matched_at)}</dd>
                </>)}
              </dl>
              {job.description && (
                <pre className="text-xs text-gray-500 whitespace-pre-wrap font-sans leading-relaxed max-h-64 overflow-y-auto bg-gray-50 rounded p-3 border border-gray-100">
                  {job.description}
                </pre>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function JobAlertsShow({ job, alert, matches, excluded, campaign, firm_relationship, search_params, leopard_results, ai_assist, leopard_search_job }: Props) {
  const [showExcluded, setShowExcluded]   = useState(false);
  const [showWeak, setShowWeak]           = useState(false);
  const [drafting, setDrafting]           = useState<number | null>(null);
  const [draftingAll, setDraftingAll]     = useState(false);
  const [confirming, setConfirming]       = useState(false);
  const [searchError, setSearchError]     = useState<string | null>(null);
  const [rematching, setRematching]       = useState(false);
  const [searchJobId, setSearchJobId]     = useState<number | null>(leopard_search_job?.id ?? null);

  useEffect(() => {
    setSearchJobId(leopard_search_job?.id ?? null);
  }, [leopard_search_job?.id]);

  // task 2dc337d2 Slice 4 — folds the whole search (enrich + specialty
  // resolve + Leopard API call) into one background job behind one status,
  // rather than tracking the AI portion separately from the Leopard fetch.
  const { generating: searching } = useAiJobStatus(searchJobId, {
    onReady:  () => router.reload(),
    onFailed: (err) => setSearchError(err ?? "Search failed"),
  });

  // Strong+Possible shown by default; Weak de-emphasized behind a toggle,
  // never removed (decision #4, plan section 4).
  const strongCount    = useMemo(() => matches.filter((m) => STRONG_TIERS.has(m.tier)).length, [matches]);
  const possibleCount  = useMemo(() => matches.filter((m) => !STRONG_TIERS.has(m.tier) && !WEAK_TIERS.has(m.tier)).length, [matches]);
  const weakMatches    = useMemo(() => matches.filter((m) => WEAK_TIERS.has(m.tier)), [matches]);
  const visibleMatches = useMemo(
    () => (showWeak ? matches : matches.filter((m) => !WEAK_TIERS.has(m.tier))),
    [matches, showWeak],
  );

  // Draft All only ever drafts Strong+Possible (Submissions::AutoGenerator's
  // gate, mirrored here) — Weak is excluded from the count so the button
  // never promises to draft candidates the backend won't.
  const undraftedMatches = matches.filter((m) => !m.submission_id && !WEAK_TIERS.has(m.tier));

  function draftSubmission(candidateId: number) {
    setDrafting(candidateId);
    router.post(`/job-alerts/${job.id}/draft_submission`, { candidate_id: candidateId }, {
      onFinish: () => setDrafting(null),
    });
  }

  function draftAll() {
    setDraftingAll(true);
    router.post(`/job-alerts/${job.id}/draft_all`, {}, {
      onFinish: () => setDraftingAll(false),
    });
  }

  // Persisted matches go stale as candidates are added/enriched after the
  // last automatic run (Aria review, Slice 4) — this is the escape hatch.
  function rematch() {
    setRematching(true);
    router.post(`/job-alerts/${job.id}/match`, {}, {
      onFinish: () => setRematching(false),
    });
  }

  async function runSearch() {
    setSearchError(null);
    try {
      const res = await fetch(`/job-alerts/${job.id}/search`, {
        method:  "POST",
        headers: { "X-CSRF-Token": csrfToken() },
      });
      const body = await res.json();
      if (res.ok && body.ai_job_status_id) {
        setSearchJobId(body.ai_job_status_id);
      } else {
        setSearchError(body.error ?? "Search failed");
      }
    } catch {
      setSearchError("Network error — could not reach Leopard");
    }
  }

  return (
    <AppLayout title={`${job.firm_name} — Job Alerts`}>
      <div className="flex items-center justify-between mb-4">
        <Link href="/job-alerts" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
          <ArrowLeft className="w-4 h-4" /> Job Alerts
        </Link>

        <div className="flex items-center gap-3">
          {ai_assist && !confirming && (
            <Button size="sm" variant="outline" onClick={rematch} disabled={rematching}
              title="Matches were computed by the last automatic run — re-match to pick up new/recently-enriched candidates">
              {rematching ? "Re-matching..." : "Re-match"}
            </Button>
          )}
          {confirming ? (
            <>
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2">
                Replace {leopard_results.length} result{leopard_results.length !== 1 ? "s" : ""} from last search?
              </p>
              <Button size="sm" onClick={() => { setConfirming(false); runSearch(); }}
                className="bg-green-700 hover:bg-green-800 text-white">
                Yes, re-search
              </Button>
              <Button variant="outline" size="sm" onClick={() => setConfirming(false)}>
                Cancel
              </Button>
            </>
          ) : (
            <>
              <Button
                size="sm"
                onClick={() => (job.last_searched_at && leopard_results.length > 0) ? setConfirming(true) : runSearch()}
                disabled={searching}
                className="bg-green-700 hover:bg-green-800 text-white"
              >
                <Search className="w-3.5 h-3.5 mr-1.5" />
                {searching ? "Searching..." : job.last_searched_at ? "Re-search Leopards" : "Search Leopards"}
              </Button>
              {searchError && <span className="text-xs text-red-600">{searchError}</span>}
            </>
          )}
        </div>
      </div>

      <JobCard
        job={job}
        alert={alert}
        undraftedCount={undraftedMatches.length}
        onDraftAll={draftAll}
        draftingAll={draftingAll}
        searchParams={search_params}
        strongCount={strongCount}
        possibleCount={possibleCount}
      />

      <FirmRelationshipCard relationship={firm_relationship} />

      {campaign && <CampaignBanner campaign={campaign} />}

      <DncBanner job={job} />

      <LeopardResultsSection results={leopard_results} lastSearchedAt={job.last_searched_at} jobId={job.id} hasCampaign={!!campaign} />

      {/* Matches */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 className="text-sm font-semibold text-gray-700">
            Matches <span className="text-gray-400 font-normal">({visibleMatches.length})</span>
          </h2>
          {weakMatches.length > 0 && (
            <Button variant="outline" size="sm" onClick={() => setShowWeak((p) => !p)}>
              {showWeak ? "Hide" : "Show"} weak matches ({weakMatches.length})
            </Button>
          )}
        </div>

        {visibleMatches.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 py-12 text-center">
            <BookOpen className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-400">
              {matches.length === 0
                ? "No candidates match this job yet — matching runs automatically, or use Re-match above."
                : "No strong or possible matches. Weak matches are hidden — show them above."}
            </p>
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
            {visibleMatches.map((m) => {
              const tcfg = TIER_CONFIG[m.tier] ?? TIER_CONFIG.weak;
              const isDrafting = drafting === m.candidate_id;

              return (
                <div key={m.candidate_id} className="px-4 py-3 flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Link href={`/candidates/${m.candidate_id}`}
                        className="text-sm font-medium text-gray-900 hover:text-green-700">
                        {m.full_name}
                      </Link>
                      <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${tcfg.className}`}>
                        {tcfg.label}
                      </span>
                      {m.submission_id && (
                        <span className="text-xs bg-blue-50 text-blue-600 border border-blue-200 px-1.5 py-0.5 rounded capitalize">
                          {m.submission_status ?? "Drafted"}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {m.firm_name}
                      {m.practice_area && <> · {m.practice_area}</>}
                      {m.jd_year && <> · JD {m.jd_year}</>}
                      {m.location && <> · {m.location}</>}
                    </p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-gray-400">{STAGE_LABELS[m.pipeline_stage] ?? m.pipeline_stage}</span>
                      {m.rationale && (
                        <>
                          <span className="text-xs text-gray-300">·</span>
                          {/* Relies on React's default text escaping — rationale is
                              AI-derived text, never rendered via dangerouslySetInnerHTML
                              (Saga note, Slice 4). */}
                          <span className="text-xs text-gray-500 italic truncate">{m.rationale}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <span className="text-xs text-gray-400">{m.days_since_update}d ago</span>
                    {ai_assist && m.email && (
                      <AskPermissionButton
                        candidate={{ first_name: m.first_name, email: m.email }}
                        job={{ firm_name: job.firm_name, title: job.title, practice_area: job.practice_area }}
                        aiAssist={ai_assist}
                      />
                    )}
                    {!m.submission_id && (
                      <button onClick={() => draftSubmission(m.candidate_id)} disabled={isDrafting}
                        className="text-xs font-medium text-green-700 border border-green-200 hover:bg-green-50 rounded px-2.5 py-1 transition-colors disabled:opacity-50">
                        {isDrafting ? "Drafting..." : "Draft Submission"}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Excluded */}
      {excluded.length > 0 && (
        <div>
          <button onClick={() => setShowExcluded((p) => !p)}
            className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-3">
            <UserX className="w-4 h-4" />
            {showExcluded ? "Hide" : "Show"} excluded candidates ({excluded.length})
          </button>

          {showExcluded && (
            <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
              {excluded.map((e) => (
                <div key={e.candidate_id} className="px-4 py-3 flex items-start gap-3 opacity-60">
                  <UserX className="w-4 h-4 mt-0.5 shrink-0 text-gray-400" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <Link href={`/candidates/${e.candidate_id}`}
                        className="text-sm font-medium text-gray-700 hover:text-green-700">
                        {e.full_name}
                      </Link>
                      <span className="text-xs bg-red-50 text-red-600 border border-red-200 px-1.5 py-0.5 rounded">
                        {e.reason}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {e.firm_name}{e.practice_area && <> · {e.practice_area}</>}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </AppLayout>
  );
}
